import React from 'react';
import type { SchemaDefinition } from '../../lib/api';
import styles from './schema-editor.module.css';

interface DisplayNameOption {
  value: string;
  label: string;
}

interface SchemaMetaCardProps {
  schema: SchemaDefinition;
  category: string;
  descriptionValue: string;
  onDescriptionChange: (value: string) => void;
  showUrlPattern: boolean;
  urlPatternValue: string;
  onUrlPatternChange: (value: string) => void;
  displayNameValue: string;
  displayNameOptions: DisplayNameOption[];
  onDisplayNameChange: (value: string) => void;
}

/**
 * Always-editable schema metadata card. Name, plural name, and schema ID are
 * server-owned and rendered read-only; description, URL pattern, and display
 * name are bound to the draft and edit in place (no edit-mode gate).
 */
export const SchemaMetaCard: React.FC<SchemaMetaCardProps> = ({
  schema,
  category,
  descriptionValue,
  onDescriptionChange,
  showUrlPattern,
  urlPatternValue,
  onUrlPatternChange,
  displayNameValue,
  displayNameOptions,
  onDisplayNameChange,
}) => {
  const pluralName = schema.pluralName || schema.plural_name || '—';

  return (
    <div className={styles.metaCardCard}>
      <div className={styles.metaCardField}>
        <span className={styles.metaCardLabel}>Name</span>
        <span className={styles.metaCardReadonlyValue}>{schema.name}</span>
        <span className={styles.metaCardHelper}>
          Set when the schema is created and cannot be changed.
        </span>
      </div>

      <div className={styles.metaCardField}>
        <span className={styles.metaCardLabel}>Plural name</span>
        <span className={styles.metaCardReadonlyValue}>{pluralName}</span>
      </div>

      <label className={styles.metaCardDescriptionField}>
        <span className={styles.metaCardLabel}>Description</span>
        <textarea
          className={styles.metaCardTextarea}
          rows={6}
          value={descriptionValue}
          onChange={e => onDescriptionChange(e.target.value)}
          placeholder="Describe what this schema represents…"
        />
      </label>

      {showUrlPattern && (
        <label className={styles.metaCardField}>
          <span className={styles.metaCardLabel}>URL pattern</span>
          <input
            className={styles.metaCardMonoInput}
            value={urlPatternValue}
            onChange={e => onUrlPatternChange(e.target.value)}
            placeholder="e.g., https://example.com/resource/{id}"
          />
          <span className={styles.metaCardHelper}>
            Use {'{fieldName}'}, or {'{referencedSchema.fieldName}'} for nested fields.
          </span>
        </label>
      )}

      <label className={styles.metaCardField}>
        <span className={styles.metaCardLabel}>Display name</span>
        <select
          className={styles.metaCardSelect}
          value={displayNameValue}
          onChange={e => onDisplayNameChange(e.target.value)}>
          <option value="">None</option>
          {displayNameOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className={styles.metaCardHelper}>
          The field used as the display name for this resource in the UI. With None, the schema name is
          used.
        </span>
      </label>

      <div className={styles.metaCardIdRow}>
        <span className={styles.metaCardIdLabel}>Schema ID</span>
        <span className={styles.metaCardIdValue}>{schema.id}</span>
        {category ? <span className={styles.metaCardIdCategory}>{category}</span> : null}
      </div>
    </div>
  );
};
